import React, { useEffect, useMemo, useRef, useState } from "react";
import Globe from "react-globe.gl";
import * as THREE from "three";
import { api } from "@/lib/api";

/**
 * QuoteGlobe
 * Live 3D visualization that reacts to whatever airports the user has
 * populated in the Instant Quote widget. Renders:
 *   • A pin for every resolved airport
 *   • Great-circle arcs between consecutive legs
 *   • A top-down jet icon that animates along the newest arc in ~1.5s
 *
 * Props:
 *   airports   — array of { icao, label? } (already validated)
 *   aircraftLabel — e.g. "G650" (shown as a callout during animation)
 *   height     — pixel height (default 420)
 */
export default function QuoteGlobe({ airports = [], aircraftLabel = "", height = 420 }) {
  const globeRef = useRef(null);
  const [resolved, setResolved] = useState([]);           // [{icao, lat, lng, label}]
  const [jetPos, setJetPos] = useState(null);             // {lat, lng, alt} while animating
  const [animKey, setAnimKey] = useState(0);              // bump when we want to re-fly
  const cacheRef = useRef(new Map());                     // icao -> {lat, lng, label}

  /* ---------- Resolve each airport ICAO -> coords (public /airports/search endpoint) ---------- */
  useEffect(() => {
    let cancelled = false;
    const uniq = [];
    const seen = new Set();
    for (const a of airports) {
      const icao = (a?.icao || "").trim().toUpperCase();
      if (!icao || seen.has(icao)) continue;
      seen.add(icao);
      uniq.push(icao);
    }
    const run = async () => {
      const out = [];
      for (const icao of uniq) {
        if (cacheRef.current.has(icao)) {
          out.push(cacheRef.current.get(icao));
          continue;
        }
        try {
          const r = await api.get("/airports/search", { params: { q: icao, limit: 1 } });
          const hit = (r.data?.results || [])[0];
          if (hit && hit.lat != null && hit.lng != null) {
            const entry = { icao, lat: Number(hit.lat), lng: Number(hit.lng), label: `${hit.icao} · ${hit.city || hit.name || ""}` };
            cacheRef.current.set(icao, entry);
            out.push(entry);
          }
        } catch { /* skip unresolved */ }
      }
      if (!cancelled) setResolved(out);
    };
    if (uniq.length > 0) run(); else setResolved([]);
    return () => { cancelled = true; };
  }, [JSON.stringify(airports.map((a) => a?.icao || ""))]);

  /* ---------- Auto-fly to the newest leg + trigger jet animation ---------- */
  useEffect(() => {
    if (!globeRef.current || resolved.length === 0) return;
    const last = resolved[resolved.length - 1];
    const prev = resolved.length >= 2 ? resolved[resolved.length - 2] : null;
    const target = prev
      ? { lat: (prev.lat + last.lat) / 2, lng: (prev.lng + last.lng) / 2, altitude: 0.9 }
      : { lat: last.lat, lng: last.lng, altitude: 1.4 };
    // Small delay so the ref-managed camera is fully initialized before we transition.
    const tid = setTimeout(() => { globeRef.current?.pointOfView(target, 1200); }, 60);
    if (prev) setAnimKey((k) => k + 1);
    return () => clearTimeout(tid);
  }, [resolved.length]);

  /* Initial camera anchored on the Americas so the globe doesn't spin from
     Africa when the user first opens the widget. */
  useEffect(() => {
    if (!globeRef.current) return;
    globeRef.current.pointOfView({ lat: 30, lng: -90, altitude: 2.2 }, 0);
    // Disable auto-rotate — the fly-to animations are the show.
    const controls = globeRef.current.controls?.();
    if (controls) { controls.autoRotate = false; controls.enableDamping = true; }
  }, []);

  useEffect(() => {
    if (resolved.length < 2 || !aircraftLabel) return;
    setAnimKey((k) => k + 1);
  }, [aircraftLabel]);

  /* ---------- Jet animation across ALL legs sequentially ----------
   * The plane flies leg 1 → leg 2 → … → last leg, then loops back to leg 1.
   * Each leg gets 2.6s + a small 0.4s pause. Runs continuously so users
   * always see the plane on some part of the itinerary.
   */
  useEffect(() => {
    if (resolved.length < 2) { setJetPos(null); return; }
    const legDuration = 2600;
    const legPause    = 400;
    const legs = [];
    for (let i = 0; i < resolved.length - 1; i++) {
      const from = resolved[i], to = resolved[i + 1];
      legs.push([from, to, legAltitude(from, to)]);
    }
    const cycleTotal = legs.length * (legDuration + legPause);
    const start = performance.now();
    let raf;
    const tick = (now) => {
      if (legs.length === 0) return;
      const elapsed = (now - start) % cycleTotal;
      const legIdx  = Math.min(legs.length - 1, Math.floor(elapsed / (legDuration + legPause)));
      const trip = legs[legIdx];
      if (!trip) return;
      const [from, to, arcAlt] = trip;
      const localT = elapsed - legIdx * (legDuration + legPause);
      if (localT < legDuration) {
        const t = localT / legDuration;
        const { lat, lng } = greatCircleInterp(from, to, t);
        // Match the arc's parabolic dome exactly: 4·arcAlt·t·(1-t) peaks at arcAlt when t=0.5.
        const alt = 4 * arcAlt * t * (1 - t);
        const brg = initialBearing({ lat, lng }, to);
        setJetPos({ lat, lng, alt, bearing: brg });
      } else {
        // Hold on the destination pin (surface-level) during the inter-leg pause.
        setJetPos({ lat: to.lat, lng: to.lng, alt: 0.005, bearing: initialBearing(from, to) });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [resolved, animKey]);

  /* ---------- Data for globe layers ---------- */
  const arcs = useMemo(() => {
    if (resolved.length < 2) return [];
    const out = [];
    for (let i = 0; i < resolved.length - 1; i++) {
      out.push({
        startLat: resolved[i].lat,   startLng: resolved[i].lng,
        endLat:   resolved[i + 1].lat, endLng: resolved[i + 1].lng,
        color: ["#4B9CFF", "#2ED573"],
        altitude: legAltitude(resolved[i], resolved[i + 1]),
      });
    }
    return out;
  }, [resolved]);

  const points = useMemo(() => resolved.map((p, i) => ({
    ...p,
    size: 0.6,
    color: i === 0 ? "#2ED573" : (i === resolved.length - 1 ? "#FF9F1C" : "#4B9CFF"),
  })), [resolved]);

  const jetObjects = useMemo(() => (jetPos ? [{
    lat: jetPos.lat, lng: jetPos.lng, alt: jetPos.alt, bearing: jetPos.bearing, label: aircraftLabel,
  }] : []), [jetPos, aircraftLabel]);

  /**
   * Build a top-down airplane sprite. Uses a canvas-drawn plane icon rendered
   * to a THREE.Sprite so it always faces the camera (proper top-down look).
   * We rotate the sprite material so the nose points along the current
   * great-circle heading — makes the animation read as a plane actually
   * flying the route.
   */
  const planeTextureRef = useRef(null);
  const getPlaneTexture = () => {
    if (planeTextureRef.current) return planeTextureRef.current;
    const size = 256;
    const c = document.createElement("canvas");
    c.width = c.height = size;
    const ctx = c.getContext("2d");
    ctx.translate(size / 2, size / 2);
    // Nose points UP so a rotation of `bearing` radians clockwise from north
    // aligns the plane with the direction of travel.
    ctx.rotate(0);
    // Body
    const grd = ctx.createLinearGradient(0, -size / 2, 0, size / 2);
    grd.addColorStop(0, "#FFFFFF");
    grd.addColorStop(1, "#E7ECF3");
    ctx.fillStyle = grd;
    ctx.strokeStyle = "#4B9CFF";
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(0, -110);                   // nose
    ctx.bezierCurveTo(28, -60, 24, 20, 18, 55);
    ctx.lineTo(48, 90);                    // tail-out right
    ctx.lineTo(44, 100);
    ctx.lineTo(10, 85);
    ctx.lineTo(6, 118);                    // tail tip right
    ctx.lineTo(-6, 118);
    ctx.lineTo(-10, 85);
    ctx.lineTo(-44, 100);
    ctx.lineTo(-48, 90);
    ctx.lineTo(-18, 55);
    ctx.bezierCurveTo(-24, 20, -28, -60, 0, -110);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // Wings
    ctx.fillStyle = "#4B9CFF";
    ctx.beginPath();
    ctx.moveTo(-8, -10);
    ctx.lineTo(-100, 30);
    ctx.lineTo(-100, 45);
    ctx.lineTo(-8, 15);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(8, -10);
    ctx.lineTo(100, 30);
    ctx.lineTo(100, 45);
    ctx.lineTo(8, 15);
    ctx.closePath();
    ctx.fill();
    // Cockpit
    ctx.fillStyle = "#0A0F16";
    ctx.beginPath();
    ctx.ellipse(0, -70, 10, 18, 0, 0, Math.PI * 2);
    ctx.fill();
    const tex = new THREE.CanvasTexture(c);
    tex.anisotropy = 4;
    tex.needsUpdate = true;
    planeTextureRef.current = tex;
    return tex;
  };

  const jetObjectFactory = (obj) => {
    const material = new THREE.SpriteMaterial({
      map: getPlaneTexture(),
      transparent: true,
      depthTest: false,
    });
    const sprite = new THREE.Sprite(material);
    // Sized so it reads clearly on the arc without dwarfing the pins.
    sprite.scale.set(3.6, 3.6, 1);
    material.rotation = -((obj?.bearing ?? 0) * Math.PI) / 180;
    // A soft cyan glow disc behind the plane makes it pop on the night texture.
    const glow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: getPlaneTexture(),
        color: 0x4B9CFF,
        transparent: true,
        opacity: 0.45,
        depthTest: false,
      }),
    );
    glow.scale.set(6.0, 6.0, 1);
    glow.material.rotation = material.rotation;
    const group = new THREE.Group();
    group.add(glow);
    group.add(sprite);
    return group;
  };

  /* ---------- Render ---------- */
  return (
    <div
      data-testid="quote-globe"
      className="relative w-full rounded-xl overflow-hidden border border-cyan/25"
      style={{ height, background: "radial-gradient(ellipse at 50% 40%, rgba(75,156,255,0.10), rgba(5,8,16,1) 65%)" }}
    >
      <Globe
        ref={globeRef}
        width={undefined}
        height={height}
        backgroundColor="rgba(0,0,0,0)"
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
        bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
        atmosphereColor="#4B9CFF"
        atmosphereAltitude={0.18}
        showAtmosphere
        // Points
        pointsData={points}
        pointLat="lat"
        pointLng="lng"
        pointColor="color"
        pointAltitude={0.02}
        pointRadius="size"
        // Arcs
        arcsData={arcs}
        arcColor="color"
        arcAltitude="altitude"
        arcStroke={0.5}
        arcDashLength={0.45}
        arcDashGap={0.15}
        arcDashAnimateTime={2200}
        // Airport labels
        labelsData={resolved}
        labelLat="lat"
        labelLng="lng"
        labelText="icao"
        labelSize={0.5}
        labelDotRadius={0.35}
        labelColor={() => "#FFFFFF"}
        labelAltitude={0.03}
        // Animated jet object
        objectsData={jetObjects}
        objectLat="lat"
        objectLng="lng"
        objectAltitude="alt"
        objectThreeObject={jetObjectFactory}
        objectLabel="label"
      />

      {/* Overlay caption */}
      <div className="absolute top-2 left-3 mono text-[10px] tracking-[0.24em] uppercase text-cyan/90 pointer-events-none">
        // LIVE ROUTE PREVIEW
      </div>
      {resolved.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-white/40 mono text-[11px] tracking-widest uppercase pointer-events-none">
          Start picking airports — we'll draw the route
        </div>
      )}
      {resolved.length >= 2 && aircraftLabel && (
        <div className="absolute bottom-2 right-3 mono text-[10px] tracking-widest uppercase text-cyan/80 flex items-center gap-1.5 bg-obsidian/60 px-2 py-1 rounded border border-cyan/25">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan animate-pulse" /> {aircraftLabel}
        </div>
      )}
    </div>
  );
}

/**
 * Great-circle interpolation between two lat/lng points.
 * Returns { lat, lng } at fraction `t` along the geodesic.
 * Standard aviation-formula slerp on the unit sphere.
 */
function greatCircleInterp(a, b, t) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const φ1 = toRad(a.lat), λ1 = toRad(a.lng);
  const φ2 = toRad(b.lat), λ2 = toRad(b.lng);
  const dφ = φ2 - φ1;
  const dλ = λ2 - λ1;
  const h = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  const δ = 2 * Math.asin(Math.min(1, Math.sqrt(h)));
  if (δ === 0) return { lat: a.lat, lng: a.lng };
  const A = Math.sin((1 - t) * δ) / Math.sin(δ);
  const B = Math.sin(t * δ) / Math.sin(δ);
  const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
  const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
  const z = A * Math.sin(φ1) + B * Math.sin(φ2);
  const φ = Math.atan2(z, Math.sqrt(x * x + y * y));
  const λ = Math.atan2(y, x);
  return { lat: toDeg(φ), lng: toDeg(λ) };
}

/**
 * Initial great-circle bearing from `a` to `b` in degrees (0..360, N=0, E=90).
 * Used to spin the plane sprite so its nose actually points at the destination.
 */
function initialBearing(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const φ1 = toRad(a.lat), φ2 = toRad(b.lat);
  const dλ = toRad(b.lng - a.lng);
  const y = Math.sin(dλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ);
  return (Math.atan2(y, x) * 180) / Math.PI;
}

/**
 * Per-leg arc altitude used both by the drawn arc AND by the plane sprite
 * so the two stay perfectly locked together. Scales with the great-circle
 * distance (in radians) and is clamped so ultra-short and trans-oceanic
 * legs both still read as domes.
 */
function legAltitude(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const φ1 = toRad(a.lat), φ2 = toRad(b.lat);
  const dλ = toRad(b.lng - a.lng);
  const dφ = φ2 - φ1;
  const h = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  const δ = 2 * Math.asin(Math.min(1, Math.sqrt(h))); // angular distance in radians (0..π)
  return Math.min(0.22, Math.max(0.05, δ * 0.18));
}
